/**
 * Fungsi murni layar korelasi.
 *
 * Split out of `screens/Correlation.tsx` not for tidiness but so it can be
 * tested without starting React: what is checked here is arithmetic — colour
 * ramp weighting, surface distance, panel chunking — and arithmetic does not
 * need a DOM in order to be wrong.
 */

/**
 * The blue-green-yellow-red rainbow that permeability sheets have used for
 * decades. Not a colour-blind-friendly ramp; kept because its readers are
 * trained on it, and the axis numbers are there regardless.
 */
export const SPECTRUM_STOPS: [number, number, number][] = [
  [70, 100, 190], [30, 150, 200], [40, 190, 150], [130, 210, 70],
  [235, 220, 60], [240, 150, 45], [220, 60, 50],
];

/** The spectrum colour at position `t` in [0,1]; clamped outside that. */
export function spectrum(t: number): string {
  const x = Math.min(0.999, Math.max(0, t)) * (SPECTRUM_STOPS.length - 1);
  const i = Math.floor(x);
  const f = x - i;
  const a = SPECTRUM_STOPS[i];
  const b = SPECTRUM_STOPS[Math.min(SPECTRUM_STOPS.length - 1, i + 1)];
  const c = a.map((v, k) => Math.round(v + (b[k] - v) * f));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

/**
 * The colour-picker palette. Literal values, not theme variables: a colour
 * sendiri oleh pengguna harus tetap sama saat lembar dicetak atau saat tema
 * because the colour has become part of the reading.
 */
export const PALETTE = [
  "#35b8c6", "#2f7fd0", "#6b5bb0", "#9b59b6", "#d6407a", "#d64545",
  "#e07b2a", "#d6a92a", "#7fc06a", "#3a9a5c", "#5f7a8a", "#7a6440",
];

/** Jarak permukaan antara dua titik, atau `null` bila salah satu tak berkoordinat. */
export function surfaceDistance(
  a: { surface_x: number | null; surface_y: number | null },
  b: { surface_x: number | null; surface_y: number | null },
): number | null {
  if (a.surface_x === null || a.surface_y === null || b.surface_x === null || b.surface_y === null)
    return null;
  const dx = a.surface_x - b.surface_x;
  const dy = a.surface_y - b.surface_y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Bagi daftar menjadi kelompok berukuran `size`; sisa terakhir boleh lebih pendek. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 1) return items.map((item) => [item]);
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Axis numbers as short as they can be without losing meaning: 0.2 stays 0.2; 150 stays 150. */
export function fmtScale(value: number): string {
  if (!Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 10) return value.toFixed(abs % 1 === 0 ? 0 : 1);
  if (abs >= 1) return value.toFixed(abs % 1 === 0 ? 0 : 1);
  return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}
